import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Star, Briefcase, CheckCircle, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const DepartmentDetailPageNew = () => {
  const navigate = useNavigate();
  const { departmentName } = useParams();
  const [locationFilter, setLocationFilter] = useState('all');

  // Department data - in production, this would come from backend
  const departmentData = {
    'art-department': {
      name: 'Art Department',
      headerImage: 'https://images.pexels.com/photos/8088386/pexels-photo-8088386.jpeg',
      description: 'The Art Department shapes the visual world of a production — from set design and props to overall visual style. They work closely with the Director and Cinematographer to ensure every frame reflects the story\'s tone, period, and mood. Roles include Production Designer, Art Director, Set Decorator, and Prop Master.',
      roles: [
        { title: 'Production Designer', description: 'Oversees the entire visual concept and design strategy' },
        { title: 'Art Director', description: 'Manages the art department and executes the design vision' },
        { title: 'Set Designer', description: 'Creates detailed plans and drawings for sets' },
        { title: 'Set Decorator', description: 'Furnishes and decorates sets with props and dressing' },
        { title: 'Prop Master', description: 'Manages all props and ensures availability during shooting' },
        { title: 'Construction Manager', description: 'Oversees the physical construction of sets' }
      ],
      keyDuties: [
        'Conceptualizing and designing the visual style of the production',
        'Creating set designs, props, and decorative elements',
        'Managing budgets for set construction and props',
        'Coordinating with directors and cinematographers',
        'Sourcing or creating period-accurate or futuristic elements',
        'Maintaining continuity in visual elements across scenes'
      ],
      professionals: [
        { name: 'Rajesh Kapoor', location: 'Mumbai, Maharashtra', experience: '12 years', specialization: 'Period Films', rating: 4.8, projects: 25 },
        { name: 'Priya Malhotra', location: 'Delhi, NCR', experience: '8 years', specialization: 'Contemporary Sets', rating: 4.9, projects: 18 },
        { name: 'Amit Shah', location: 'Mumbai, Maharashtra', experience: '15 years', specialization: 'Large Scale Productions', rating: 4.7, projects: 32 },
        { name: 'Neha Reddy', location: 'Hyderabad, Telangana', experience: '6 years', specialization: 'Indie Films', rating: 4.6, projects: 12 }
      ]
    },
    'camera-department': {
      name: 'Camera Department',
      headerImage: 'https://images.pexels.com/photos/10395639/pexels-photo-10395639.jpeg',
      description: 'The Camera Department captures every frame of the film, handling camera operation, lens selection, and focus. Led by the Director of Photography (DP), this team translates the director\'s vision into visual language through framing, movement, and lighting collaboration.',
      roles: [
        { title: 'Director of Photography (DP)', description: 'Chief creative and technical leader of camera and lighting' },
        { title: 'Camera Operator', description: 'Operates the camera during filming' },
        { title: 'First AC (Focus Puller)', description: 'Maintains sharp focus during shots' },
        { title: 'Second AC (Clapper Loader)', description: 'Manages camera equipment and marks takes' },
        { title: 'DIT (Digital Imaging Technician)', description: 'Manages digital workflow and color on set' },
        { title: 'Steadicam Operator', description: 'Specialist in stabilized camera movement' }
      ],
      keyDuties: [
        'Planning shot composition and camera movements',
        'Operating cameras and maintaining technical quality',
        'Managing focus, exposure, and camera settings',
        'Coordinating with lighting department',
        'Maintaining camera equipment and digital workflow',
        'Ensuring visual consistency across shots'
      ],
      professionals: [
        { name: 'Vikram Singh', location: 'Mumbai, Maharashtra', experience: '18 years', specialization: 'Feature Films', rating: 4.9, projects: 42 },
        { name: 'Arjun Menon', location: 'Bangalore, Karnataka', experience: '10 years', specialization: 'Commercials', rating: 4.8, projects: 28 },
        { name: 'Karan Patel', location: 'Mumbai, Maharashtra', experience: '14 years', specialization: 'Music Videos', rating: 4.7, projects: 35 }
      ]
    },
    'sound-department': {
      name: 'Sound Department',
      headerImage: 'https://images.pexels.com/photos/23657253/pexels-photo-23657253.jpeg',
      description: 'The Sound Department captures clean, high-quality audio during production and shapes the film\'s sonic experience — dialogue, ambient sound, and effects. Roles include Sound Recordist, Boom Operator, and Sound Mixer.',
      roles: [
        { title: 'Sound Mixer', description: 'Chief audio engineer on set, manages all sound recording' },
        { title: 'Boom Operator', description: 'Positions microphones to capture optimal audio' },
        { title: 'Sound Assistant', description: 'Supports sound mixer with equipment and cabling' },
        { title: 'Production Sound Recordist', description: 'Records and monitors all production audio' }
      ],
      keyDuties: [
        'Recording clean dialogue and ambient sound',
        'Managing microphone placement and audio levels',
        'Monitoring and troubleshooting audio issues',
        'Coordinating with other departments for quiet during takes',
        'Maintaining and operating sound equipment',
        'Creating detailed audio logs and notes'
      ],
      professionals: [
        { name: 'Ravi Kumar', location: 'Chennai, Tamil Nadu', experience: '16 years', specialization: 'Feature Films', rating: 4.9, projects: 38 },
        { name: 'Suresh Iyer', location: 'Mumbai, Maharashtra', experience: '12 years', specialization: 'Documentaries', rating: 4.7, projects: 24 }
      ]
    },
    'direction': {
      name: 'Direction',
      headerImage: 'https://images.pexels.com/photos/8088390/pexels-photo-8088390.jpeg',
      description: 'The Direction team leads the creative vision of the production, guiding actors, coordinating with every department, and making final creative calls on how a story is told on screen. Includes Director, Creative Heads, and Vision Leads.',
      roles: [
        { title: 'Director', description: 'Chief creative leader overseeing all aspects of production' },
        { title: 'Creative Director', description: 'Shapes the overall creative vision and aesthetic' },
        { title: 'Vision Lead', description: 'Ensures consistency of creative direction' },
        { title: 'Assistant Director (Creative)', description: 'Supports director with creative decisions' }
      ],
      keyDuties: [
        'Defining and executing the creative vision',
        'Directing actors and guiding performances',
        'Making final decisions on shot composition and coverage',
        'Coordinating with all departments',
        'Managing the storytelling and pacing',
        'Ensuring the film stays true to the script and vision'
      ],
      professionals: [
        { name: 'Aditya Chopra', location: 'Mumbai, Maharashtra', experience: '20 years', specialization: 'Feature Films', rating: 4.9, projects: 15 },
        { name: 'Meera Nair', location: 'Bangalore, Karnataka', experience: '12 years', specialization: 'Indie Cinema', rating: 4.8, projects: 22 }
      ]
    },
    'makeup-hair': {
      name: 'Makeup & Hair',
      headerImage: 'https://images.pexels.com/photos/5732998/pexels-photo-5732998.jpeg',
      description: 'The Makeup & Hair team transforms actors into their characters — from everyday looks to prosthetics and period-accurate styling — while ensuring continuity across scenes.',
      roles: [
        { title: 'Makeup Artist', description: 'Creates character looks and maintains makeup continuity' },
        { title: 'Hair Stylist', description: 'Designs and maintains hairstyles for characters' },
        { title: 'Prosthetics Artist', description: 'Creates special effects makeup and prosthetics' },
        { title: 'Makeup Department Head', description: 'Oversees all makeup and hair operations' }
      ],
      keyDuties: [
        'Designing character-specific makeup and hairstyles',
        'Applying and maintaining makeup during shoots',
        'Creating prosthetics and special effects makeup',
        'Ensuring continuity across scenes and days',
        'Managing makeup schedules and call times',
        'Collaborating with costume and lighting departments'
      ],
      professionals: [
        { name: 'Kavita Desai', location: 'Mumbai, Maharashtra', experience: '14 years', specialization: 'Period Films', rating: 4.9, projects: 31 },
        { name: 'Rohit Sharma', location: 'Delhi, NCR', experience: '10 years', specialization: 'Prosthetics', rating: 4.7, projects: 19 }
      ]
    },
    'sfx-vfx': {
      name: 'SFX / VFX',
      headerImage: 'https://images.pexels.com/photos/8102674/pexels-photo-8102674.jpeg',
      description: 'The SFX/VFX team creates visual effects, practical effects, and digital enhancements — from explosions and stunts to CGI compositing and 3D modeling done in post-production.',
      roles: [
        { title: 'VFX Supervisor', description: 'Oversees all visual effects work' },
        { title: '3D Artist', description: 'Creates 3D models and animations' },
        { title: 'Compositor', description: 'Combines visual elements into final shots' },
        { title: 'SFX Coordinator', description: 'Manages practical effects on set' },
        { title: 'Rotoscope Artist', description: 'Isolates and manipulates elements in footage' },
        { title: 'Match Move Artist', description: 'Tracks camera movement for CGI integration' }
      ],
      keyDuties: [
        'Planning and executing visual effects sequences',
        'Creating CGI elements and 3D models',
        'Compositing live-action and digital elements',
        'Managing practical effects on set',
        'Coordinating with director and cinematographer',
        'Ensuring seamless integration of effects'
      ],
      professionals: [
        { name: 'Abhishek Roy', location: 'Bangalore, Karnataka', experience: '16 years', specialization: 'CGI', rating: 4.9, projects: 28 },
        { name: 'Sanjay Mehta', location: 'Mumbai, Maharashtra', experience: '13 years', specialization: 'Compositing', rating: 4.8, projects: 34 }
      ]
    },
    'production': {
      name: 'Production',
      headerImage: 'https://images.unsplash.com/photo-1485846234645-a62644f84728',
      description: 'The Production team manages the logistics that keep a shoot running — scheduling, budgeting, locations, and permits. Roles include Line Producer, Production Manager, and Production Coordinators.',
      roles: [
        { title: 'Line Producer', description: 'Manages production budget and logistics' },
        { title: 'Production Manager', description: 'Oversees day-to-day production operations' },
        { title: 'Production Coordinator', description: 'Handles scheduling and communication' },
        { title: 'Location Manager', description: 'Scouts and manages filming locations' },
        { title: 'Production Assistant', description: 'Supports all production activities' }
      ],
      keyDuties: [
        'Managing production budgets and expenses',
        'Scheduling shoots and coordinating crew',
        'Securing locations and permits',
        'Handling logistics and transportation',
        'Managing contracts and paperwork',
        'Ensuring production stays on schedule and budget'
      ],
      professionals: [
        { name: 'Anita Kapoor', location: 'Mumbai, Maharashtra', experience: '18 years', specialization: 'Feature Films', rating: 4.9, projects: 45 },
        { name: 'Rajiv Malhotra', location: 'Delhi, NCR', experience: '11 years', specialization: 'Commercials', rating: 4.7, projects: 27 }
      ]
    },
    'assistant-directors': {
      name: 'Assistant Directors',
      headerImage: 'https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf',
      description: 'Assistant Directors manage the operational flow of the set — scheduling scenes, coordinating cast and crew movements, and maintaining set discipline. Includes 1st AD, 2nd AD, and Floor Managers.',
      roles: [
        { title: '1st Assistant Director', description: 'Chief AD, manages set operations and scheduling' },
        { title: '2nd Assistant Director', description: 'Handles cast logistics and call sheets' },
        { title: '3rd Assistant Director', description: 'Manages background actors and crowd control' },
        { title: 'Floor Manager', description: 'Controls set access and maintains discipline' }
      ],
      keyDuties: [
        'Creating and managing shooting schedules',
        'Coordinating cast and crew movements',
        'Maintaining set discipline and safety',
        'Managing background actors and extras',
        'Communicating schedule changes',
        'Ensuring efficient use of shooting time'
      ],
      professionals: [
        { name: 'Vikram Bhat', location: 'Mumbai, Maharashtra', experience: '15 years', specialization: 'Feature Films', rating: 4.8, projects: 38 },
        { name: 'Priya Singh', location: 'Hyderabad, Telangana', experience: '9 years', specialization: 'Web Series', rating: 4.7, projects: 21 }
      ]
    },
    'lighting-department': {
      name: 'Lighting Department',
      headerImage: 'https://images.pexels.com/photos/30397430/pexels-photo-30397430.jpeg',
      description: 'The Lighting Department designs and executes the lighting setup for every scene, shaping mood, depth, and visibility. Includes Gaffers, Lighting Technicians (Best Boy), and Electricians.',
      roles: [
        { title: 'Gaffer', description: 'Chief lighting technician, designs lighting setups' },
        { title: 'Best Boy Electric', description: 'Assistant to gaffer, manages lighting crew' },
        { title: 'Lighting Technician', description: 'Sets up and operates lighting equipment' },
        { title: 'Electrician', description: 'Handles power distribution and electrical safety' }
      ],
      keyDuties: [
        'Designing lighting plans for each scene',
        'Setting up and rigging lighting equipment',
        'Managing power distribution on set',
        'Adjusting lights during shooting',
        'Collaborating with cinematographer',
        'Ensuring electrical safety on set'
      ],
      professionals: [
        { name: 'Ramesh Kumar', location: 'Chennai, Tamil Nadu', experience: '17 years', specialization: 'Studio Lighting', rating: 4.9, projects: 41 },
        { name: 'Deepak Verma', location: 'Mumbai, Maharashtra', experience: '13 years', specialization: 'Location Lighting', rating: 4.8, projects: 33 }
      ]
    },
    'post-production': {
      name: 'Post Production',
      headerImage: 'https://images.pexels.com/photos/11553189/pexels-photo-11553189.jpeg',
      description: 'The Post Production team takes raw footage and shapes it into the final film — editing, color grading, sound mixing, and finishing. Includes Editors, Colorists, and Post-Production Supervisors.',
      roles: [
        { title: 'Post-Production Supervisor', description: 'Oversees the entire post-production process' },
        { title: 'Editor', description: 'Assembles and refines the final cut' },
        { title: 'Colorist', description: 'Performs color correction and grading' },
        { title: 'Sound Designer', description: 'Creates and mixes the film\'s soundscape' },
        { title: 'VFX Artist', description: 'Adds visual effects in post' }
      ],
      keyDuties: [
        'Managing post-production workflow and schedule',
        'Coordinating editing, color, and sound',
        'Reviewing and approving deliverables',
        'Managing technical specifications',
        'Liaising with director and producers',
        'Ensuring timely completion of final product'
      ],
      professionals: [
        { name: 'Anil Sharma', location: 'Mumbai, Maharashtra', experience: '19 years', specialization: 'Feature Films', rating: 4.9, projects: 47 },
        { name: 'Neha Khanna', location: 'Bangalore, Karnataka', experience: '11 years', specialization: 'Web Series', rating: 4.7, projects: 29 }
      ]
    },
    'script-department': {
      name: 'Script Department',
      headerImage: 'https://images.pexels.com/photos/1117132/pexels-photo-1117132.jpeg',
      description: 'The Script Department ensures narrative and continuity accuracy throughout production — tracking scene details, dialogue changes, and script revisions. Includes Scriptwriters, Continuity Supervisors, and Script Coordinators.',
      roles: [
        { title: 'Script Supervisor', description: 'Ensures continuity and tracks script changes' },
        { title: 'Scriptwriter', description: 'Writes and revises screenplay' },
        { title: 'Script Coordinator', description: 'Manages script distribution and revisions' },
        { title: 'Continuity Supervisor', description: 'Tracks continuity of props, costumes, and actions' }
      ],
      keyDuties: [
        'Tracking continuity across scenes and takes',
        'Noting dialogue and action changes',
        'Managing script revisions and distribution',
        'Creating detailed continuity reports',
        'Coordinating with editing department',
        'Ensuring narrative consistency'
      ],
      professionals: [
        { name: 'Sanjana Reddy', location: 'Hyderabad, Telangana', experience: '14 years', specialization: 'Feature Films', rating: 4.8, projects: 36 },
        { name: 'Kunal Mehra', location: 'Mumbai, Maharashtra', experience: '10 years', specialization: 'Web Series', rating: 4.7, projects: 24 }
      ]
    },
    'editing': {
      name: 'Editing',
      headerImage: 'https://images.unsplash.com/photo-1528109966604-5a6a4a964e8d',
      description: 'Editors assemble raw footage into a coherent narrative, shaping pacing, rhythm, and emotional impact in the edit bay. Includes Editors, Assistant Editors, and Online Editors.',
      roles: [
        { title: 'Editor', description: 'Assembles and crafts the final narrative' },
        { title: 'Assistant Editor', description: 'Organizes footage and supports lead editor' },
        { title: 'Online Editor', description: 'Performs final technical edits and finishing' },
        { title: 'Offline Editor', description: 'Creates rough cuts and assembly edits' }
      ],
      keyDuties: [
        'Assembling footage into coherent sequences',
        'Creating rough cuts and fine cuts',
        'Shaping pacing and rhythm',
        'Collaborating with director on creative decisions',
        'Managing media and project files',
        'Delivering final edited product'
      ],
      professionals: [
        { name: 'Arjun Kapoor', location: 'Mumbai, Maharashtra', experience: '16 years', specialization: 'Feature Films', rating: 4.9, projects: 39 },
        { name: 'Riya Patel', location: 'Bangalore, Karnataka', experience: '12 years', specialization: 'Commercials', rating: 4.8, projects: 31 }
      ]
    }
  };

  const currentDept = departmentData[departmentName] || departmentData['art-department'];

  const filteredProfessionals = locationFilter === 'all' 
    ? currentDept.professionals 
    : currentDept.professionals.filter(prof => prof.location.includes(locationFilter));

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/departments')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Image */}
      <section className="relative h-80 overflow-hidden">
        <img 
          src={currentDept.headerImage} 
          alt={currentDept.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent"></div>
        <div className="absolute bottom-8 left-0 right-0 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-3 bg-accent/90 text-white border-0">{currentDept.name}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">{currentDept.name}</h1>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg text-muted-foreground leading-relaxed">
                {currentDept.description}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8">Roles & Positions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {currentDept.roles.map((role, index) => (
                <motion.div
                  key={role.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full bg-card border-border hover:border-accent/50 transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="bg-accent/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">{role.title}</h3>
                          <p className="text-sm text-muted-foreground">{role.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Duties */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8">Key Duties & Responsibilities</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {currentDept.keyDuties.map((duty, index) => (
                <motion.div
                  key={`duty-${duty.slice(0, 30)}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">{duty}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Professionals */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-2">Registered Professionals</h2>
                <p className="text-muted-foreground">
                  Showing {filteredProfessionals.length} {filteredProfessionals.length === 1 ? 'professional' : 'professionals'}
                </p>
              </div>
              <div className="w-64">
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="bg-card">
                    <SelectValue placeholder="Filter by location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="Mumbai">Mumbai</SelectItem>
                    <SelectItem value="Delhi">Delhi</SelectItem>
                    <SelectItem value="Bangalore">Bangalore</SelectItem>
                    <SelectItem value="Chennai">Chennai</SelectItem>
                    <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProfessionals.map((prof, index) => (
                <motion.div
                  key={prof.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full bg-card border-border hover:border-accent/50 transition-all hover:shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Badge className="bg-accent/10 text-accent border-accent/30">
                          <Star className="w-3 h-3 mr-1 fill-accent" />
                          {prof.rating}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{prof.projects} projects</span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{prof.name}</h3>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4 text-accent" />
                          {prof.location}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Briefcase className="w-4 h-4 text-accent" />
                          {prof.experience}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        <strong>Specialization:</strong> {prof.specialization}
                      </p>
                      <Button className="w-full bg-accent hover:bg-accent/90 text-white" size="sm">
                        View Profile
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Looking for {currentDept.name} Professionals?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Post your requirements and connect with verified professionals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold group"
                onClick={() => navigate('/post-project')}
              >
                Post a Job in {currentDept.name}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-accent text-accent hover:bg-accent hover:text-primary-foreground"
                onClick={() => navigate('/auth')}
              >
                Join as a {currentDept.name} Professional
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DepartmentDetailPageNew;
